let lastToken = 0;
let lastAppliedToken = 0;

const outstanding = new Set<number>();

const hasNewerClaim = (token: number) => {
  let newer = false;

  outstanding.forEach((candidate) => {
    if (candidate > token) newer = true;
  });

  return newer;
};

/**
 * Orders the viewport moves that settle on a timer — centring and edge
 * correction — so two of them cannot fight over the same viewport.
 *
 * Ordering is by request, not by application: a caller claims a token when the
 * move is asked for and checks it again before applying, so the most recently
 * requested move wins even if it is the slowest to settle. Claims are tracked
 * individually rather than by a high-water mark, because any number of them can
 * be outstanding at once and they do not resolve in the order they were made.
 *
 * Every claim must end in exactly one of `settleViewportAnimation` (it moved
 * the viewport) or `abandonViewportAnimation` (it did not, for any reason).
 * A claim left outstanding blocks every older one from ever applying.
 */
export const claimViewportAnimation = () => {
  lastToken += 1;
  outstanding.add(lastToken);

  return lastToken;
};

export const isLatestViewportAnimation = (token: number) =>
  outstanding.has(token) && token > lastAppliedToken && !hasNewerClaim(token);

/** Records that this claim moved the viewport, retiring every older claim. */
export const settleViewportAnimation = (token: number) => {
  lastAppliedToken = Math.max(lastAppliedToken, token);

  outstanding.forEach((candidate) => {
    if (candidate <= lastAppliedToken) outstanding.delete(candidate);
  });
};

/** Gives up a claim without having moved the viewport. */
export const abandonViewportAnimation = (token: number) => {
  outstanding.delete(token);
};
